import {
	Button,
	Checkbox,
	DatePicker,
	Input,
	Modal,
	Popconfirm,
	Radio,
	Table,
	Tag,
	message,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { request } from "../../../utils/request";
import {
	SENT_FIELD,
	WEBHOOK_FIELD,
	getBirthdayVN,
	getCustomerName,
	getPeriodKeys,
	isValidVnMobile,
	normalizePhone,
	sendBirthdayZns,
} from "../../../utils/utilsBirthday";

const PAGE_SIZE_FETCH = 200;
const MAX_FETCH = 2000;

const getHeaders = () => ({
	"X-Access-Token": localStorage.getItem("token"),
	"Content-Type": "application/json",
});

const formatSentTime = (value) =>
	value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "";

// Khớp ngày sinh với ngày đang xem. 29/2 được tính vào 28/2 khi năm không nhuận.
const isBirthdayOnDay = (birthday, target) => {
	if (birthday.month() !== target.month()) return false;
	if (birthday.date() === target.date()) return true;
	return (
		birthday.month() === 1 &&
		birthday.date() === 29 &&
		target.date() === 28 &&
		target.daysInMonth() === 28
	);
};

export default function BirthdayZns() {
	const [messageApi, contextHolder] = message.useMessage();
	const [loading, setLoading] = useState(false);
	const [mode, setMode] = useState("ngay");
	const [dateValue, setDateValue] = useState(dayjs());
	const [monthValue, setMonthValue] = useState(dayjs());
	const [keyword, setKeyword] = useState("");
	const [onlyInvalid, setOnlyInvalid] = useState(false);
	const [rawList, setRawList] = useState([]);
	const [truncated, setTruncated] = useState(false);
	const [appExfields, setAppExfields] = useState({});
	const [sendingKey, setSendingKey] = useState(null);
	const [editing, setEditing] = useState(null);
	const [phoneInput, setPhoneInput] = useState("");
	const [savingPhone, setSavingPhone] = useState(false);

	const target = mode === "ngay" ? dateValue : monthValue;
	const viewYear = target?.year();

	const handleGetAppData = async () => {
		try {
			const idApp = localStorage.getItem("id_app");
			const res = await request.get("api/app", {
				params: {
					q: JSON.stringify({ _id: idApp }),
					notfields: "__v",
				},
				headers: getHeaders(),
			});
			setAppExfields(res?.data?.[0]?.exfields || {});
		} catch (error) {
			console.log(error);
		}
	};

	// Lọc thô trên server theo tháng ±1 (ngày sinh lưu UTC có thể lệch sang tháng
	// liền kề), sau đó lọc chính xác theo giờ Việt Nam ở client.
	const handleGetCustomers = async () => {
		if (!target) return;
		setLoading(true);
		try {
			const idApp = localStorage.getItem("id_app");
			const month = target.month() + 1;
			const months = [(month + 10) % 12 + 1, month, (month % 12) + 1];
			const q = {
				trang_thai: 1,
				status: true,
				isbuyer: true,
				kh_yn: true,
				loai_kh: { $nin: ["Vendor", "Staff"] },
				$expr: { $in: [{ $month: "$ngay_sinh" }, months] },
			};
			let all = [];
			let page = 1;
			let hitCap = false;
			while (true) {
				const res = await request.get(`api/${idApp}/customer`, {
					params: {
						q: JSON.stringify(q),
						limit: PAGE_SIZE_FETCH,
						page,
					},
					headers: getHeaders(),
				});
				const rows = Array.isArray(res?.data) ? res.data : [];
				all = all.concat(rows);
				if (rows.length < PAGE_SIZE_FETCH) break;
				if (all.length >= MAX_FETCH) {
					hitCap = true;
					break;
				}
				page += 1;
			}
			setRawList(all);
			setTruncated(hitCap);
		} catch (error) {
			console.log(error);
			messageApi.open({ type: "error", content: "Tải danh sách thất bại" });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		handleGetAppData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		handleGetCustomers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, dateValue?.format("YYYY-MM-DD"), monthValue?.format("YYYY-MM")]);

	const rows = useMemo(() => {
		if (!target) return [];
		const kw = keyword.trim().toLowerCase();
		const result = [];
		rawList.forEach((customer) => {
			const birthday = getBirthdayVN(customer?.ngay_sinh);
			if (!birthday) return;
			const matched =
				mode === "ngay"
					? isBirthdayOnDay(birthday, target)
					: birthday.month() === target.month();
			if (!matched) return;

			const valid = isValidVnMobile(customer?.dien_thoai);
			if (onlyInvalid && valid) return;
			if (kw) {
				const text =
					`${getCustomerName(customer)} ${customer?.ma_kh ?? ""} ${customer?.dien_thoai ?? ""}`.toLowerCase();
				if (!text.includes(kw)) return;
			}
			result.push({
				...customer,
				_birthday: birthday,
				_valid: valid,
				_keys: getPeriodKeys(birthday, viewYear),
			});
		});
		result.sort(
			(a, b) =>
				a._birthday.month() - b._birthday.month() ||
				a._birthday.date() - b._birthday.date() ||
				getCustomerName(a).localeCompare(getCustomerName(b), "vi"),
		);
		return result;
	}, [rawList, mode, target, keyword, onlyInvalid, viewYear]);

	const summary = useMemo(() => {
		const sentField = SENT_FIELD[mode === "ngay" ? "ngay" : "thang"];
		const valid = rows.filter((r) => r._valid).length;
		const sent = rows.filter((r) => {
			const key = mode === "ngay" ? r._keys.day : r._keys.month;
			return r?.exfields?.[sentField] === key;
		}).length;
		return { total: rows.length, valid, invalid: rows.length - valid, sent };
	}, [rows, mode]);

	const updateRowExfields = (id, exfields) => {
		setRawList((prev) =>
			prev.map((item) => (item._id === id ? { ...item, exfields } : item)),
		);
	};

	const handleSend = async (record, type) => {
		const linkWebhook = appExfields?.[WEBHOOK_FIELD[type]];
		if (!linkWebhook) {
			messageApi.open({
				type: "warning",
				content: "Chưa cấu hình webhook, vào /setting để nhập link",
			});
			return;
		}
		const key = `${record._id}_${type}`;
		setSendingKey(key);
		try {
			const { marked, exfields } = await sendBirthdayZns({
				type,
				customer: record,
				birthday: record._birthday,
				periodKey: type === "thang" ? record._keys.month : record._keys.day,
				linkWebhook,
				idApp: localStorage.getItem("id_app"),
				token: localStorage.getItem("token"),
			});
			updateRowExfields(record._id, exfields);
			messageApi.open(
				marked
					? { type: "success", content: "Đã gửi ZNS" }
					: {
							type: "warning",
							content:
								"Đã gửi ZNS nhưng chưa ghi được cờ 'đã gửi', cần kiểm tra lại",
						},
			);
		} catch (error) {
			console.log(error);
			messageApi.open({ type: "error", content: "Gửi ZNS thất bại" });
		} finally {
			setSendingKey(null);
		}
	};

	const openEditPhone = (record) => {
		setEditing(record);
		setPhoneInput(record?.dien_thoai || "");
	};

	const handleSavePhone = async () => {
		if (!isValidVnMobile(phoneInput)) {
			messageApi.open({
				type: "error",
				content: "SĐT không hợp lệ (cần số di động Việt Nam 10 số)",
			});
			return;
		}
		const phone = normalizePhone(phoneInput);
		setSavingPhone(true);
		try {
			await request.put(
				`api/${localStorage.getItem("id_app")}/customer/${editing._id}`,
				{ _id: editing._id, dien_thoai: phone },
				{ headers: getHeaders() },
			);
			setRawList((prev) =>
				prev.map((item) =>
					item._id === editing._id ? { ...item, dien_thoai: phone } : item,
				),
			);
			messageApi.open({ type: "success", content: "Đã cập nhật SĐT" });
			setEditing(null);
		} catch (error) {
			console.log(error);
			messageApi.open({ type: "error", content: "Cập nhật SĐT thất bại" });
		} finally {
			setSavingPhone(false);
		}
	};

	const renderSendCell = (record, type) => {
		const field = SENT_FIELD[type];
		const periodKey = type === "thang" ? record._keys.month : record._keys.day;
		const sent = record?.exfields?.[field] === periodKey;
		const sentAt = formatSentTime(record?.exfields?.[`${field}_luc`]);
		const sending = sendingKey === `${record._id}_${type}`;
		return (
			<div className="flex flex-col gap-1 items-start">
				{sent ? (
					<Tag color="green" title={sentAt}>
						Đã gửi {sentAt && `(${sentAt})`}
					</Tag>
				) : (
					<Tag>Chưa gửi</Tag>
				)}
				<Popconfirm
					title={sent ? "Gửi lại ZNS?" : "Gửi ZNS ngay?"}
					description={`${getCustomerName(record)} - ${normalizePhone(record?.dien_thoai)}`}
					okText="Gửi"
					cancelText="Hủy"
					disabled={!record._valid}
					onConfirm={() => handleSend(record, type)}
				>
					<Button
						size="small"
						disabled={!record._valid}
						loading={sending}
						className={sent ? "" : "btn_primary"}
					>
						{sent ? "Gửi lại" : "Gửi"}
					</Button>
				</Popconfirm>
			</div>
		);
	};

	const columns = [
		{
			title: "Mã / Tên",
			key: "ten",
			render: (_, record) => (
				<div>
					<div className="font-medium">{getCustomerName(record)}</div>
					<div className="text-xs text-gray-500">{record?.ma_kh}</div>
				</div>
			),
		},
		{
			title: "SĐT",
			dataIndex: "dien_thoai",
			key: "dien_thoai",
			render: (value) => value || <span className="text-gray-400">(trống)</span>,
		},
		{
			title: "Ngày sinh",
			key: "ngay_sinh",
			render: (_, record) => (
				<div>
					<div>{record._birthday.format("DD/MM/YYYY")}</div>
					<div className="text-xs text-gray-400">
						DB: {String(record?.ngay_sinh)}
					</div>
				</div>
			),
		},
		{
			title: "Gửi theo tháng",
			key: "thang",
			render: (_, record) => renderSendCell(record, "thang"),
		},
		{
			title: "Gửi theo ngày",
			key: "ngay",
			render: (_, record) => renderSendCell(record, "ngay"),
		},
		{
			title: "Ghi chú",
			key: "ghi_chu",
			render: (_, record) =>
				record._valid ? null : <Tag color="red">SĐT không hợp lệ</Tag>,
		},
		{
			title: "Thao tác",
			key: "action",
			render: (_, record) => (
				<Button size="small" onClick={() => openEditPhone(record)}>
					Sửa SĐT
				</Button>
			),
		},
	];

	return (
		<div className="p-4">
			{contextHolder}
			<h2 className="text-lg font-semibold text-[#186b9e] mb-1">
				Kiểm tra sinh nhật khách hàng (ZNS)
			</h2>
			<div className="text-sm text-gray-500 mb-3">
				Chỉ khách hiện hữu. Ngày sinh tính theo giờ Việt Nam. Trang kiểm tra dữ
				liệu và gửi thủ công, chưa gửi tự động.
			</div>

			<div className="flex flex-wrap gap-3 items-center mb-3">
				<Radio.Group
					value={mode}
					onChange={(e) => setMode(e.target.value)}
					optionType="button"
					buttonStyle="solid"
					options={[
						{ label: "Theo ngày", value: "ngay" },
						{ label: "Theo tháng", value: "thang" },
					]}
				/>
				{mode === "ngay" ? (
					<DatePicker
						value={dateValue}
						onChange={(v) => v && setDateValue(v)}
						allowClear={false}
						format="DD/MM/YYYY"
					/>
				) : (
					<DatePicker
						picker="month"
						value={monthValue}
						onChange={(v) => v && setMonthValue(v)}
						allowClear={false}
						format="MM/YYYY"
					/>
				)}
				<Input.Search
					allowClear
					placeholder="Tìm tên / mã / SĐT"
					style={{ width: 240 }}
					onSearch={setKeyword}
					onChange={(e) => !e.target.value && setKeyword("")}
				/>
				<Checkbox
					checked={onlyInvalid}
					onChange={(e) => setOnlyInvalid(e.target.checked)}
				>
					Chỉ SĐT không hợp lệ
				</Checkbox>
				<Button onClick={handleGetCustomers} loading={loading}>
					Tải lại
				</Button>
			</div>

			<div className="flex flex-wrap gap-2 mb-3">
				<Tag>Tổng: {summary.total}</Tag>
				<Tag color="blue">SĐT hợp lệ: {summary.valid}</Tag>
				<Tag color="red">Không hợp lệ: {summary.invalid}</Tag>
				<Tag color="green">Đã gửi: {summary.sent}</Tag>
				{!appExfields?.[WEBHOOK_FIELD[mode === "ngay" ? "ngay" : "thang"]] && (
					<Tag color="orange">
						Chưa cấu hình webhook {mode === "ngay" ? "theo ngày" : "theo tháng"}{" "}
						(vào /setting)
					</Tag>
				)}
				{truncated && (
					<Tag color="orange">
						Danh sách thô vượt {MAX_FETCH} bản ghi, kết quả có thể thiếu
					</Tag>
				)}
			</div>

			<Table
				rowKey="_id"
				size="small"
				bordered
				loading={loading}
				columns={columns}
				dataSource={rows}
				pagination={{ pageSize: 50, showSizeChanger: false }}
				scroll={{ x: 900 }}
			/>

			<Modal
				open={!!editing}
				title={`Sửa SĐT - ${editing ? getCustomerName(editing) : ""}`}
				okText="Lưu"
				cancelText="Hủy"
				confirmLoading={savingPhone}
				onOk={handleSavePhone}
				onCancel={() => setEditing(null)}
			>
				<div className="flex flex-col gap-2">
					<Input
						value={phoneInput}
						onChange={(e) => setPhoneInput(e.target.value)}
						onPressEnter={handleSavePhone}
						placeholder="Ví dụ 0707261102"
					/>
					{phoneInput && !isValidVnMobile(phoneInput) && (
						<span className="text-red-500 text-sm">
							SĐT không hợp lệ
						</span>
					)}
					<span className="text-xs text-gray-500">
						SĐT được ghi thẳng vào hồ sơ khách hàng.
					</span>
				</div>
			</Modal>
		</div>
	);
}
